import { formatTimeDifference } from '@/lib/utils'

import {
  type MappingJobStep,
  MappingJobStepStatus,
} from '@droidmap/mapping-job-service-contract'
import { OdmStage } from '@droidmap/shared-odm-settings'

/**
 * Friendly labels for each ODM pipeline stage. Keyed off the shared
 * {@link OdmStage} enum — the backend pre-seeds one `MappingJobStep` per stage
 * with `name` set to the raw enum value (see the mapping-job service's
 * `Object.values(OdmStage)` seed), so a step's `name` maps straight through
 * here.
 */
export const STAGE_LABELS: Record<OdmStage, string> = {
  [OdmStage.STEP0_DATASET]: 'Dataset',
  [OdmStage.STEP1_SPLIT]: 'Split',
  [OdmStage.STEP2_MERGE]: 'Merge',
  [OdmStage.STEP3_OPEN_SFM]: 'Structure from Motion',
  [OdmStage.STEP4_OPEN_MVS]: 'Dense Reconstruction',
  [OdmStage.STEP5_FILTER_POINTS]: 'Filter Points',
  [OdmStage.STEP6_MESHING]: 'Meshing',
  [OdmStage.STEP7_TEXTURING]: 'Texturing',
  [OdmStage.STEP8_GEOREFERENCING]: 'Georeferencing',
  [OdmStage.STEP9_DEM]: 'Elevation Model',
  [OdmStage.STEP10_ORTHOPHOTO]: 'Orthophoto',
  [OdmStage.STEP11_REPORT]: 'Report',
  [OdmStage.STEP12_POST_PROCESS]: 'Post-processing',
}

/** Canonical pipeline order — the enum's declaration order. */
const STAGE_ORDER: readonly string[] = Object.values(OdmStage)

/**
 * A friendly label for a step name. Falls back to a de-slugged version of the
 * raw name so an unrecognised/newer stage still renders something readable
 * rather than a blank.
 */
export const stageLabel = (name: string): string => {
  const known = STAGE_LABELS[name as OdmStage]
  if (known) return known
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

/**
 * Sort index for a step name in canonical pipeline order. Unknown names sort
 * last (and keep their relative input order thanks to a stable sort).
 */
export const stageOrderIndex = (name: string): number => {
  const i = STAGE_ORDER.indexOf(name)
  return i === -1 ? Number.MAX_SAFE_INTEGER : i
}

const toDate = (value: Date | string | undefined): Date | undefined => {
  if (!value) return undefined
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? undefined : d
}

/** A stage row ready for the timeline UI. */
export interface StageProgress {
  /** Raw step name (an {@link OdmStage} value for known stages). */
  name: string
  label: string
  status: MappingJobStepStatus
  startTime?: Date
  endTime?: Date
  /**
   * Elapsed/total time in ms: finished stages use `end - start`; a running
   * stage uses `now - start`; pending stages have none.
   */
  durationMs?: number
}

/**
 * Project raw `MappingJobStep`s into ordered, UI-ready {@link StageProgress}
 * rows. Steps are sorted into canonical pipeline order regardless of the order
 * the API returned them. `now` is injectable for deterministic tests.
 */
export const toStageProgress = (
  steps: MappingJobStep[],
  now: number = Date.now(),
): StageProgress[] =>
  [...steps]
    .sort((a, b) => stageOrderIndex(a.name) - stageOrderIndex(b.name))
    .map((step) => {
      const startTime = toDate(step.startTime)
      const endTime = toDate(step.endTime)
      let durationMs: number | undefined
      if (startTime && endTime) {
        durationMs = Math.max(0, endTime.getTime() - startTime.getTime())
      } else if (startTime && step.status === MappingJobStepStatus.RUNNING) {
        durationMs = Math.max(0, now - startTime.getTime())
      }
      return {
        name: step.name,
        label: stageLabel(step.name),
        status: step.status,
        startTime,
        endTime,
        durationMs,
      }
    })

export interface OverallProgress {
  completed: number
  total: number
  /** Completed / total as a 0–100 integer. */
  percent: number
}

/** Overall completion derived from how many steps have finished. */
export const deriveOverallProgress = (
  steps: MappingJobStep[],
): OverallProgress => {
  const total = steps.length
  const completed = steps.filter(
    (s) => s.status === MappingJobStepStatus.COMPLETED,
  ).length
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0
  return { completed, total, percent }
}

/**
 * Whether the job is still doing work. A single failed step is terminal (the
 * pipeline aborts), and an all-completed job is done; anything else with steps
 * remaining is active.
 */
export const isJobActive = (steps: MappingJobStep[]): boolean => {
  if (steps.length === 0) return false
  if (steps.some((s) => s.status === MappingJobStepStatus.FAILED)) return false
  return !steps.every((s) => s.status === MappingJobStepStatus.COMPLETED)
}

/**
 * react-query `refetchInterval` gate: poll every `intervalMs` while the job is
 * active, otherwise stop (`false`). Used so the progress view stops hammering
 * the API the moment the job reaches a terminal state.
 */
export const getStepsRefetchInterval = (
  steps: MappingJobStep[] | undefined,
  intervalMs: number,
): number | false => {
  if (!steps) return false
  return isJobActive(steps) ? intervalMs : false
}

/** Human duration string (`1m 23s`) for a stage, or `undefined` when unknown. */
export const formatStepDuration = (
  durationMs: number | undefined,
): string | undefined => {
  if (durationMs === undefined) return undefined
  return formatTimeDifference(0, durationMs)
}
